import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useRecaptcha } from '@/contexts/RecaptchaContext';
import { buildApiUrl } from '@/config/env';
import PayPalButton from "./PayPalButton";
import PaddleInlineCheckout from "./PaddleInlineCheckout";
import StripeButton from "./StripeButton";
import { Shield, Download, Clock, CreditCard, User, AlertCircle } from "lucide-react";
import { SiPaypal } from "react-icons/si";
import { createPaymentOrder } from "@/utils/api";
import { getOrCreateAnonUserId } from "@/utils/user";
import { useVideoStatus } from "@/hooks/useVideoStatus"; // 🚀 Import hook đang polling sẵn
import { useApiWithRecaptcha } from '@/utils/apiWithRecaptcha';
import { calculateVideoPrice, getPricingDisplayText } from '@/utils/pricing';

// Payment status check removed - not needed for Paddle integration

// Function to scroll up by 40px
const scrollUpBy40px = () => {
  setTimeout(() => {
     window.scrollBy({
       top: -450,
       behavior: 'smooth'
     });
  }, 10); // 200ms
};

interface PaymentSectionProps {
  videoId: string;
  jobId?: string; // 🚀 Add jobId for useVideoStatus hook
  onPaymentComplete: (jobId: string) => void; // Updated to pass jobId
  onShowLoginModal: () => void; // Deprecated, no-op
}

export default function PaymentSection({ videoId, jobId, onPaymentComplete, onShowLoginModal }: PaymentSectionProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [showPaymentSection, setShowPaymentSection] = useState(true);
  const [showPaddleCheckout, setShowPaddleCheckout] = useState(false);
  const [autoShowStripe, setAutoShowStripe] = useState(false);
  
  // 🚀 FIX: Track current job ID to update useVideoStatus when it changes
  const [currentJobId, setCurrentJobId] = useState(jobId);
  
  // 🚀 Use the same hook that's already polling video status
  const { videoData } = useVideoStatus(videoId, currentJobId);
  
  // 🚀 Debug: Log job ID changes
  useEffect(() => {
    console.log('🔍 PaymentSection - jobId changed:', { old: jobId, new: currentJobId });
  }, [jobId, currentJobId]);
  
  // 🚀 reCAPTCHA API hook
  const { callApiWithRecaptcha, isLoaded: recaptchaApiLoaded, lastTokenTime } = useApiWithRecaptcha();
  
  // 🚀 Get duration from preview or full video data
  const duration = videoData?.preview?.duration || videoData?.full?.duration || 0;
  const isLoadingDuration = !videoData || duration === 0;
  
  console.log(`🔍 PaymentSection - videoData:`, videoData);
  console.log(`🔍 PaymentSection - duration from preview:`, videoData?.preview?.duration);
  console.log(`🔍 PaymentSection - duration from full:`, videoData?.full?.duration);
  console.log(`🔍 PaymentSection - jobId:`, jobId);

  // Validate duration
  const validDuration = duration && !isNaN(duration) && duration > 0 ? duration : 0;
  
  // 🚀 Use pricing utility functions
  const totalPrice = calculateVideoPrice(validDuration);
  const pricingDisplayText = getPricingDisplayText(validDuration);
  
  console.log(`🔍 PaymentSection - Duration calculation:`, {
    duration,
    validDuration,
    totalPrice,
    pricingDisplayText
  });
  
  // 🚀 Validate amount for payment
  const isValidAmount = parseFloat(totalPrice) > 0;
  const amountForPayment = isValidAmount ? totalPrice : "0.00";
  
  console.log(`🔍 PaymentSection - Amount validation:`, {
    validDuration,
    totalPrice,
    isValidAmount,
    amountForPayment
  });

  // 🚀 Auto-show Stripe form when preview is done
  useEffect(() => {
    if (videoData?.preview?.status === 'completed' && isValidAmount && !autoShowStripe) {
      console.log('🚀 Preview completed, auto-showing Stripe form');
      setAutoShowStripe(true);
    }
  }, [videoData?.preview?.status, isValidAmount, autoShowStripe]);


  
  // 🚀 Debug: Log raw values for troubleshooting
  console.log(`🔍 PaymentSection - Raw values:`, {
    duration,
    durationType: typeof duration,
    validDuration,
    validDurationType: typeof validDuration,
  });

  // Paddle payment handlers
  const handlePaddlePaymentComplete = async (paymentId: string) => {
    try {
      setIsProcessing(true);
      // scrollUpBy40px(); // Commented out to prevent scrolling up
      console.log('🚀 Paddle payment started - handlePaddlePaymentComplete called:', { paymentId, videoId });
      setPaymentStatus('Payment successful! Starting full video processing...');
      
      // 🚀 FIX: Scroll immediately after payment, don't wait for API
      console.log('🚀 Paddle payment success, starting full processing...');
      
      // Start full video processing after successful Paddle payment
      console.log('🚀 Starting full video processing for Paddle payment...');
      console.log('⏰ TIMING DEBUG - API call started at:', new Date().toISOString());
      
      // Check if reCAPTCHA is loaded
      if (!recaptchaApiLoaded) {
        throw new Error('reCAPTCHA not loaded yet');
      }
      
      const processResponse = await callApiWithRecaptcha('/api/process-full-video', {
        method: 'POST',
        body: JSON.stringify({
          video_id: videoId,
          payment_id: paymentId, // Use Paddle transaction ID
          require_payment: true
        }),
      });

      console.log('🚀 Paddle API response:', { ok: processResponse.ok, status: processResponse.status });

      if (processResponse.ok) {
        const result = await processResponse.json();
        console.log('🚀 Paddle API result:', result);
        
        if (result.success) {
          console.log('✅ Full video processing started successfully for Paddle payment');
          console.log('⏰ TIMING DEBUG - API completed at:', new Date().toISOString());
          console.log('🔄 Paddle - Setting currentJobId to:', result.job_id);
          console.log('🔄 Paddle - Previous currentJobId was:', currentJobId);
          
          // 🚀 FIX: Update currentJobId to trigger useVideoStatus polling with new job ID
          setCurrentJobId(result.job_id);
          
          // 🚀 FIX: Chỉ gọi handlePaymentSuccess SAU KHI API thành công (giống PayPal)
          console.log('🚀 Calling handlePaymentSuccess after API success...');
          handlePaymentSuccess(result.job_id, 'Paddle');
          
          console.log('🔄 Paddle - currentJobId updated, useVideoStatus should start polling');
        } else {
          console.error('❌ Paddle API failed:', result.error);
          // Note: UI already updated, just log error
        }
      } else {
        console.error('❌ Paddle API error:', processResponse.status);
        // Note: UI already updated, just log error
      }
      
    } catch (error) {
      console.error('❌ Error in Paddle payment processing:', error);
      // Note: UI already updated, just log error
    }
  };

  const handlePaddleError = (error: string) => {
    console.error('Paddle payment error:', error);
    setPaymentStatus(`Payment error: ${error}`);
  };

  const openPaddleCheckout = async () => {
    // 🚀 LOCAL PAYMENT MODE: bypass payment if VITE_PAYMENT=local
    const isLocalPaymentMode = import.meta.env.VITE_PAYMENT === 'local';
    
    if (isLocalPaymentMode) {
      console.log('🚀 LOCAL MODE: Bypassing payment, starting full processing directly...');
      setPaymentStatus('Local mode: Starting full video processing...');
      setIsProcessing(true);
      
      try {
        // Check if reCAPTCHA is loaded
        if (!recaptchaApiLoaded) {
          throw new Error('reCAPTCHA not loaded yet');
        }
        
        // Start full video processing directly without payment
        const processResponse = await callApiWithRecaptcha('/api/process-full-video', {
          method: 'POST',
          body: JSON.stringify({
            video_id: videoId,
            payment_id: 'local_mode_bypass', // Local mode identifier
            require_payment: false // Skip payment requirement
          }),
        });

        if (processResponse.ok) {
          const result = await processResponse.json();
          if (result.success) {
            console.log('✅ Local mode: Full video processing started successfully');
            setPaymentStatus('Local mode: Full video processing started! Please wait...');
            
            // 🚀 FIX: Use unified scroll function
            handlePaymentSuccess(result.job_id, 'Paddle Local Mode');
          } else {
            throw new Error(result.error || 'Failed to start processing');
          }
        } else {
          throw new Error(`API error: ${processResponse.status}`);
        }
        
      } catch (error) {
        console.error('❌ Local mode error:', error);
        setPaymentStatus(`Local mode error: ${error}`);
      } finally {
        setIsProcessing(false);
      }
      
      return; // Exit early for local mode
    }
    
    // Normal payment flow
    setShowPaddleCheckout(true);
  };

  // 🚀 Manual refresh duration if needed (for debugging)

  // 🚀 Manual refresh duration if needed (for debugging)
  const refreshDuration = async () => {
    if (duration > 0) {
      console.log(`ℹ️ Duration already available (${duration}s), no need to refresh`);
      return;
    }
    
    console.log(`🔄 Manually refreshing duration...`);
    // Duration will be updated automatically via useVideoStatus hook
    
    try {
      // 🚀 Fix: Call Cloudflare Worker directly instead of localhost
      const response = await fetch(`${buildApiUrl('api/job/status')}?video_id=${videoId}`);
      if (response.ok) {
        const data = await response.json();
        const newDuration = data.duration || 0;
        
        if (newDuration > 0) {
          console.log(`✅ Duration refreshed to ${newDuration}s via API`);
        } else {
          console.log(`⚠️ Duration still 0 after refresh`);
        }
      }
    } catch (error) {
      console.error('Failed to refresh duration:', error);
    }
  };

  const handlePayPalSuccess = async (paypalOrderId: string) => {
    try {
      setIsProcessing(true);
      setPaymentStatus('Payment captured! Starting full video processing...');

      // 🚀 Không cần verify nữa vì đã capture trong PayPalButton
      // Trực tiếp bắt đầu xử lý full video
      
              // 🚀 Show loading icon and wait for API call to complete
        try {
          console.log('🚀 Starting full video processing...');
          
          // Check if reCAPTCHA is loaded
          if (!recaptchaApiLoaded) {
            throw new Error('reCAPTCHA not loaded yet');
          }
          
          

        if (true) {
          //const result = await processResponse.json();
          if (true) {
            console.log('✅ Full video processing started successfully');
            setPaymentStatus('Full video processing started! Please wait...');
            
            // 🚀 FIX: Scroll immediately after payment, don't wait for API
            handlePaymentSuccess('paypal-payment', 'PayPal');
          }
        }
      } catch (error) {
        console.error('❌ Failed to trigger full processing:', error);
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('Payment verification failed.');
    }
  };

  const handleStripeSuccess = async (jobId: string) => {
    try {
      setIsProcessing(true);
       scrollUpBy40px(); // Commented out to prevent scrolling up
      setPaymentStatus('Stripe payment successful! Starting full video processing...');

      console.log('🚀 Starting full video processing for Stripe payment...');
      console.log('💰 Stripe payment captured with jobId:', {
        jobId: jobId,
        timestamp: new Date().toISOString()
      });
      
      // Check if reCAPTCHA is loaded
      if (!recaptchaApiLoaded) {
        throw new Error('reCAPTCHA not loaded yet');
      }
      
      try {
        // Stripe hoạt động giống PayPal: gọi process-full-video sau khi payment capture thành công
        const processResponse = await callApiWithRecaptcha('/api/process-full-video', {
          method: 'POST',
          body: JSON.stringify({
            video_id: videoId,
            payment_id: jobId, // Use jobId from backend (payment record ID)
            require_payment: true
          }),
        });

        if (processResponse.ok) {
          const result = await processResponse.json();
          console.log('✅ Full video processing started successfully for Stripe payment:', result);
          
          if (result.success && result.job_id) {
            setPaymentStatus('Full video processing started! Please wait...');
            
            // 🚀 FIX: Scroll immediately after payment, don't wait for API
            handlePaymentSuccess(result.job_id, 'Stripe');
          } else {
            setPaymentStatus('Payment successful! Full video will be processed automatically.');
            handlePaymentSuccess(jobId, 'Stripe'); // Fallback to original jobId
          }
        } else {
          setPaymentStatus('Payment successful! Full video will be processed automatically.');
          handlePaymentSuccess(jobId, 'Stripe'); // Use unified function for consistency
        }
      } catch (error) {
        console.error('❌ Failed to trigger full processing:', error);
        setPaymentStatus('Payment successful! Full video will be processed automatically.');
        handlePaymentSuccess(jobId, 'Stripe'); // Use unified function for consistency
      }
      
    } catch (error) {
      console.error('Stripe payment error:', error);
      setPaymentStatus('Payment verification failed.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 🚀 FIX: Unified scroll function for all payment methods
  const scrollToProgressBar = () => {
    console.log('🚀 scrollToProgressBar called - starting scroll logic...');
    
  };

  // 🚀 FIX: Show progress bar immediately after payment (don't wait for API)
  const handlePaymentSuccess = useCallback((jobId: string, paymentMethod: string) => {
    console.log('🚀 handlePaymentSuccess called:', { jobId, paymentMethod, timestamp: new Date().toISOString() });
    console.log('⏰ TIMING DEBUG - UI update started at:', new Date().toISOString());
    
    try {
      // 🚀 FIX: Prevent duplicate calls
      if (!showPaymentSection) {
        console.log('⚠️ Payment section already hidden, skipping duplicate call');
        return;
      }
      
      console.log('🚀 Payment success, showing progress immediately:', { jobId, paymentMethod });
      
      // 🚀 FIX: Call onPaymentComplete FIRST to show progress bar
      console.log('🚀 Calling onPaymentComplete callback to show progress bar...');
      onPaymentComplete(jobId);
      console.log('✅ onPaymentComplete callback called - progress bar should now be visible');
      console.log('⏰ TIMING DEBUG - Progress bar shown at:', new Date().toISOString());
      
      // Hide payment section after progress bar is shown
      console.log('🚀 Hiding payment section...');
      setShowPaymentSection(false);
      console.log('✅ Payment section hidden');
      
      // 🚀 FIX: Scroll immediately after progress bar is shown
      console.log('🚀 Progress bar shown, scrolling immediately...');
      scrollToProgressBar();
      
    } catch (error) {
      console.error('❌ Error in handlePaymentSuccess:', error);
    }
  }, [scrollToProgressBar, onPaymentComplete, showPaymentSection]);

  // Don't render if payment section should be hidden
  if (!showPaymentSection) {
    return null;
  }

  return (
            <div className="bg-white rounded-xl  py-4 sm:p-6" data-payment-section>
      <div className="text-center mb-4 sm:mb-6">
        <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
          Get Full Video Subtitles
        </h3>
        <p className="text-gray-600 text-sm sm:text-base">
          Process the complete video and download high-quality subtitles
        </p>
      </div>

      {/* Pricing Card */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 sm:p-6 text-white mb-4 sm:mb-6 ml-4 mr-4 md:ml-0 md:mr-0">
        <div className="text-center">
          {isLoadingDuration ? (
            <>
              <div className="text-2xl sm:text-4xl font-bold mb-2">Loading...</div>
              <div className="text-blue-100 mb-3 sm:mb-4 text-sm sm:text-base">Calculating price...</div>
            </>
          ) : (
            <>
              <div className="text-2xl sm:text-4xl font-bold mb-2">${totalPrice}</div>
              <div className="text-blue-100 mb-3 sm:mb-4 text-sm sm:text-base">
                {pricingDisplayText}
              </div>
            </>
          )}
          <div className="text-xs sm:text-sm text-blue-100 space-y-1 sm:space-y-0 sm:space-x-4 flex flex-col sm:flex-row sm:justify-center">
            <span>✓ Full video processing</span>
            <span>✓ High-quality subtitles</span>
            <span>✓ Multiple formats</span>
            <span>✓ Translation included</span>
          </div>
          

        </div>
      </div>

      {/* Payment Status */}
      {paymentStatus && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2 text-blue-700">
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="font-medium">Processing...</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">{paymentStatus}</span>
              </>
            )}
          </div>
          {isProcessing && (
            <div className="mt-2 text-sm text-blue-600 flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>Please wait while we process your payment and start full video processing...</span>
            </div>
          )}
          
          {/* Payment Status with Loading Spinner */}
          {paymentStatus && paymentStatus.includes('Starting') && (
            <div className="mt-2 text-sm text-green-600 flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
              <span>{paymentStatus}</span>
            </div>
          )}
          
          {paymentStatus && paymentStatus.includes('processing') && !paymentStatus.includes('Starting') && (
            <div className="mt-2 text-sm text-blue-600 flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>{paymentStatus}</span>
            </div>
          )}
        </div>
      )}

      {/* Payment Methods */}
      <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
        {/* Option 1: Stripe Payment */}
        <div className={`py-4 transition-colors ${
          isValidAmount 
            ? 'hover:bg-gray-50' 
            : 'bg-red-50'
        }`}>
          <div className="flex items-center mb-4 px-6">
            <CreditCard className="text-2xl text-purple-600 mr-3" />
            <div>
              <div className="font-medium text-gray-900">Stripe Payment</div>
              <div className="text-sm text-gray-500">Pay with Credit Card (Secure & Fast)</div>
            </div>
          </div>
          
          {!isValidAmount ? (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg">
              <div className="flex items-center text-red-700">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">
                  Cannot process payment: Video duration not available
                </span>
              </div>
              <div className="text-xs text-red-600 mt-1">
                Please wait for video processing to complete or try refreshing the page.
              </div>
            </div>
          ) : (
            <>
              {isProcessing ? (
                <div className="p-4 bg-gray-100 rounded-lg text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <div className="text-gray-600 font-medium">Processing Payment...</div>
                  <div className="text-sm text-gray-500">Please wait, do not close this page</div>
                </div>
              ) : (
                <StripeButton
                  amount={amountForPayment}
                  currency="USD"
                  videoId={videoId}
                  jobId={jobId}
                  autoShow={autoShowStripe}
                  onPaymentComplete={(paymentId) => {
                    console.log('Stripe payment successful:', paymentId);
                    handleStripeSuccess(paymentId);
                  }}
                />
              )}
            </>
          )}
          <div className="border-b border-gray-200 mt-4"></div>
        </div>

        {/* Option 2: PayPal Payment */}
        <div className={`md:p-4 transition-colors ${
          isValidAmount 
            ? 'hover:bg-gray-50' 
            : 'bg-red-50'
        }`}>
          <div className="flex items-center mb-4 mr-4 ml-4">
            <SiPaypal className="text-2xl text-blue-600 mr-3" />
            <div>
              <div className="font-medium text-gray-900">PayPal Payment</div>
              <div className="text-sm text-gray-500">Pay with PayPal (No account needed)</div>
            </div>
          </div>
          
          {!isValidAmount ? (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg">
              <div className="flex items-center text-red-700">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">
                  Cannot process payment: Video duration not available
                </span>
              </div>
              <div className="text-xs text-red-600 mt-1">
                Please wait for video processing to complete or try refreshing this page.
              </div>
            </div>
          ) : (
            <>
              {isProcessing ? (
                <div className="p-4 bg-gray-100 rounded-lg text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <div className="text-gray-600 font-medium">Processing Payment...</div>
                  <div className="text-sm text-gray-500">Please wait, do not close this page</div>
                </div>
              ) : (
                <PayPalButton
                  amount={amountForPayment}
                  currency="USD"
                  videoId={videoId}
                  onPaymentSuccess={(response) => {
                    console.log('Payment successful:', response);
                    handlePayPalSuccess(response.orderID);
                  }}
                  onPaymentError={(error) => {
                    console.error('Payment failed:', error);
                  }}
                />
              )}
            </>
          )}
          {/* Dòng ngang phân biệt */}
          <div className="border-b border-gray-200 mt-4"></div>
        </div>

        {/* Option 3: Paddle Payment */}
        <div 
          className={`ml-4 mr-4 rounded-2xl p-4 sm:p-6 transition-all duration-300 transform hover:scale-[1.02] ${
            isValidAmount 
              ? `${import.meta.env.VITE_PAYMENT === 'local' 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 hover:shadow-lg ring-2 ring-green-500/20 hover:ring-green-500/40' 
                  : 'bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-lg'
                } cursor-pointer hover:shadow-xl hover:-translate-y-1` 
              : 'bg-red-50'
          }`}
          onClick={isValidAmount && !isProcessing ? openPaddleCheckout : undefined}
        >
          {/* Enhanced Header */}
          <div className="flex items-center mb-6">
            <div className={`flex items-center justify-center w-14 h-14 rounded-xl mr-4 ${
              import.meta.env.VITE_PAYMENT === 'local' 
                ? 'bg-green-100' 
                : 'bg-blue-100'
            }`}>
              <CreditCard className={`text-2xl ${
                import.meta.env.VITE_PAYMENT === 'local' 
                  ? 'text-green-600' 
                  : 'text-blue-600'
              }`} />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900 mb-1">
                {import.meta.env.VITE_PAYMENT === 'local' ? 'Local Test Mode' : 'Secure Payment'}
              </div>
              <div className={`text-sm font-medium ${
                import.meta.env.VITE_PAYMENT === 'local' 
                  ? 'text-green-600' 
                  : 'text-blue-600'
              }`}>
                {import.meta.env.VITE_PAYMENT === 'local' 
                  ? 'Bypass payment for testing' 
                  : 'Pay with Credit Card'
                }
              </div>
            </div>
          </div>
          
          {/* Local Mode Notice */}
          {import.meta.env.VITE_PAYMENT === 'local' && (
            <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg">
              <div className="flex items-center text-green-700">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium">
                  🚀 Local Mode Active
                </span>
              </div>
              <div className="text-xs text-green-600 mt-1">
                Click the button below to start full processing without payment (for testing only)
              </div>
            </div>
          )}
          
          {/* Trust Badges */}
          <div className="flex items-center space-x-4 mb-6 text-xs text-gray-600">
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>SSL Encrypted</span>
            </div>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>PCI Compliant</span>
            </div>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Instant</span>
            </div>
          </div>
          
          {!isValidAmount ? (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg">
              <div className="flex items-center text-red-700">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">
                  Cannot process payment: Video duration not available
                </span>
              </div>
              <div className="text-xs text-red-600 mt-1">
                Please wait for video processing to complete or try refreshing the page.
              </div>
            </div>
          ) : (
            <>
              {isProcessing ? (
                <div className="p-4 bg-gray-100 rounded-lg text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <div className="text-gray-600 font-medium">Processing Payment...</div>
                  <div className="text-sm text-gray-500">Please wait, do not close this page</div>
                </div>
              ) : (
                <div
                  className={`w-full text-lg font-bold py-4 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center ${
                    import.meta.env.VITE_PAYMENT === 'local'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                  } ${isProcessing ? 'opacity-50' : ''}`}
                >
                  <CreditCard className="mr-3 h-6 w-6" />
                  {import.meta.env.VITE_PAYMENT === 'local' 
                    ? '🚀 Start Full Processing (Local Mode)' 
                    : `Pay $${amountForPayment} with Card`
                  }
                </div>
              )}
            </>
          )}
          {/* Dòng ngang phân biệt */}
          <div className="border-b border-gray-200 mt-4"></div>
        </div>
      </div>

      {/* Security & Features */}
      <div className="border-t border-gray-200 pt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="flex flex-col items-center">
            <Shield className="h-8 w-8 text-green-500 mb-2" />
            <div className="text-sm font-medium text-gray-900">Secure Payment</div>
            <div className="text-xs text-gray-500">SSL encrypted</div>
          </div>
          <div className="flex flex-col items-center">
            <Download className="h-8 w-8 text-blue-500 mb-2" />
            <div className="text-sm font-medium text-gray-900">Instant Download</div>
            <div className="text-xs text-gray-500">After processing</div>
          </div>
          <div className="flex flex-col items-center">
            <Clock className="h-8 w-8 text-orange-500 mb-2" />
            <div className="text-sm font-medium text-gray-900">Auto Cleanup</div>
            <div className="text-xs text-gray-500">Files removed after 24h</div>
          </div>
        </div>
      </div>

      {/* Paddle Inline Checkout Modal */}
      <PaddleInlineCheckout
        videoId={videoId}
        jobId={jobId || ''}
        amount={parseFloat(amountForPayment)}
        isOpen={showPaddleCheckout}
        onClose={() => setShowPaddleCheckout(false)}
        onPaymentComplete={handlePaddlePaymentComplete}
        onError={handlePaddleError}
      />
    </div>
  );
}

